import React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import useChart from '../../../../hooks/useChart';
import { Chart } from '../../../../types/Chart/Chart';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import { useUpdateUserDoc } from '../../../../hooks/useUpdateUserDoc';
import { useForm } from '@tanstack/react-form';
import { NavForward } from '../../../../components/buttons/NavForward';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { EULA, EULAheader, EULAInstructions, EULASectionHeader, EULASectionText } from '../../../../components/EULA';
export const Route = createFileRoute('/_authenticated/$patientId/consent/forms')({
  component: FormsPage,
});
import { Button } from '../../../../components/buttons/Button';
import { NavArrowRight, NavArrowDown } from 'iconoir-react';
import { FormsSignatureArea } from '../../../../components';
import { ConsentForCare, ConsentSectionText, ConsentSubSectionText } from '../../../../components/CONSENT';

import { IsSigningObject } from '../../../../types/Signatures/IsSigningObject';
import { HIPAASectionText } from '../../../../components/HIPAA/hipaaSectionText';
import { HIPAA } from '../../../../components/HIPAA/HIPAA';
import { FormInput } from '../../../../components/forms/FormInput';
import { NextSection } from '../../../../components/buttons/NextSection';
import { setDisabledPages } from '../../../../data/navigation/utilities/setDisabledPages';
import { getNextPage } from '../../../../data/navigation/getNextPage';
import { Heading } from '../../../../components/Heading';
import { setInitialConciergeGuidedNavObject } from '../../../../data/navigation/setInitialConciergeGuidedNavObject';
import * as Sentry from '@sentry/browser';
import Checkbox from '../../../../components/forms/Checkbox';

function FormsPage() {
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const { isFetched: patientInfoIsFetched } = patientDocQuery;
  const patientInfo = patientDocQuery.data as PatientDoc;
  const [attestedEULA, setAttestedEULA] = React.useState(false);
  const [attestedHipaa, setAttestedHipaa] = React.useState(false);
  const [attestedConsentForCare, setAttestedConsentForCare] = React.useState(false);
  const { mutate } = useUpdateChartSection();
  const [eulaOpen, setEulaOpen] = React.useState(false);
  const [hipaaOpen, setHipaaOpen] = React.useState(false);
  const [consentForCareOpen, setConsentForCareOpen] = React.useState(false);
  const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [isSigning, setIsSigning] = React.useState({
    patientFinal: false,
    witness1: false,
    witness2: false,
    hipaa: false,
    consent: false,
    forms: false,
    abnMedicare: false,
    abn: false,
    aob1: false,
    aob2: false,
  } as IsSigningObject);
  const [canText, setCanText] = React.useState<string>('no');
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();

  const handleCanTextStatusChange = (status: string) => {
    setCanText(status);
    mutatePatientDoc(
      {
        patientId: patientId,
        data: {
          canText: canText === 'yes' ? false : true,
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  React.useEffect(() => {
    if (patientId && patientInfoIsFetched && patientInfo?.navType?.base === 'concierge' && !patientInfo?.navObj) {
      setInitialConciergeGuidedNavObject(patientId);
    }
  }, [patientId, patientInfo]);

  React.useEffect(() => {
    if (patientId && patientInfoIsFetched && patientInfo?.canText === true) {
      setCanText('yes');
    }
  }, [patientId, patientInfo]);

  // GET NEXT PAGE URL
  React.useEffect(() => {
    if (chart && patientInfo?.navObj) {
      (async () => {
        const awaitedUrl = await getNextPage({
          currentPage: 'forms',
          chart: chart,
          patientInfo: patientInfo,
        });
        setNextPageURL(awaitedUrl);
      })();
    }
  }, [chart, patientInfo]);
  // NAVIGATION: USE FULLY QUALIFIED PATHS TO KEEP TYPESCRIPT HAPPY IN LINE 25
  // const prevPage = undefined;

  const form = useForm({
    defaultValues: {
      firstName: chart?.PATIENT_INFO_1?.firstName ? chart.PATIENT_INFO_1.firstName : '',
      middleName: chart?.PATIENT_INFO_1?.middleInitial ? chart.PATIENT_INFO_1.middleInitial : '',
      lastName: chart?.PATIENT_INFO_1?.lastName ? chart.PATIENT_INFO_1.lastName : '',
      exceptions: chart?.HIPAA_AUTH?.exceptions ? chart.HIPAA_AUTH.exceptions : '',
      signature1DataURI: chart?.HIPAA_AUTH?.signature1DataURI ? chart.HIPAA_AUTH.signature1DataURI : '',
    },
    onSubmit: async ({ value }) => {
      mutate(
        {
          patientId: patientId,
          docData: {
            HIPAA_AUTH: value,
          },
        },
        {
          onSuccess: () => {
            // alert("Successfully updated document");
          },
          onError: (error) => {
            Sentry.captureException(error);
            alert(`Error updating document: ${error.message}`); // TELL THE USER
            console.error(error); // GET THE STACKTRACE
          },
        },
      );
    },
  });

  const handleEulaAttestationCheckboxClick = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          EULA: {
            agreed: !attestedEULA,
          },
        },
      },
      {
        onSuccess: () => {
          // alert("Successfully updated document");
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  const handleHipaaAttestationCheckboxClick = () => {
    setAttestedHipaa(!attestedHipaa);
  };
  const handleConsentForCareAttestationCheckboxClick = () => {
    setAttestedConsentForCare(!attestedConsentForCare);
  };

  React.useEffect(() => {
    if (chart && chart.EULA?.agreed) {
      setAttestedEULA(true);
    }

    if (chart && chart.HIPAA_AUTH && chart.HIPAA_AUTH.signature1DataURI?.length > 0) {
      setAttestedHipaa(true);
    }

    if (chart && chart.CONSENT_FOR_CARE && chart.CONSENT_FOR_CARE.signature1DataURI?.length > 0) {
      setAttestedConsentForCare(true);
    }
    if (chart && patientInfo) {
      setDisabledPages(patientInfo, chart);
    }
  }, [chart, patientInfo]);

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId) {
    return (
      <>
        <div id="forms-page" className="flex flex-col items-center">
          <Heading id="forms-page-header" textContent="Consent Forms" />
          <div id="forms-page-main" className="flex w-5/6">
            <div id="forms-page-content" className="flex flex-col items-start">
              <div id="eula-area">
                <div className="flex flex-row items-center">
                  {eulaOpen ? (
                    <NavArrowDown onClick={() => setEulaOpen(!eulaOpen)} />
                  ) : (
                    <NavArrowRight onClick={() => setEulaOpen(!eulaOpen)} />
                  )}
                  <p className="ml-2 text-2xl font-bold">END USER LICENSE AGREEMENT</p>
                </div>
                {eulaOpen && (
                  <div className="h-[65vh] overflow-scroll">
                    <EULAheader text={EULA.text[0]} />
                    <div className="">
                      <EULAInstructions text={EULA.text[1]} />
                      <EULASectionHeader text={EULA.text[2]} />
                      <EULASectionText text={EULA.text[3]} />
                      <EULASectionText text={EULA.text[4]} />
                      <EULASectionHeader text={EULA.text[5]} />
                      <EULASectionHeader text={EULA.text[6]} />
                      <EULASectionHeader text={EULA.text[7]} />
                      <EULASectionText text={EULA.text[8]} />
                      <EULASectionText text={EULA.text[9]} />
                      <EULASectionText text={EULA.text[10]} />
                      <EULASectionText text={EULA.text[11]} />
                      <EULASectionText text={EULA.text[12]} />
                      <EULASectionText text={EULA.text[13]} />
                      <EULASectionHeader text={EULA.text[14]} />
                      <EULASectionText text={EULA.text[15]} />
                      <EULASectionText text={EULA.text[16]} />
                      <EULASectionText text={EULA.text[17]} />
                      <EULASectionText text={EULA.text[18]} />
                      <EULASectionText text={EULA.text[19]} />
                      <EULASectionHeader text={EULA.text[20]} />
                      <EULASectionHeader text={EULA.text[21]} />
                      <EULASectionHeader text={EULA.text[22]} />
                      <EULASectionHeader text={EULA.text[23]} />
                      <EULASectionHeader text={EULA.text[24]} />
                    </div>
                  </div>
                )}
                <div id="eula-attestation-checkbox-container" className="my-4 ml-4 flex w-full flex-row justify-center">
                  <Checkbox
                    id="eula-attestation-checkbox"
                    name="eula-attestation"
                    onChange={() => {
                      handleEulaAttestationCheckboxClick();
                    }}
                    checked={attestedEULA}
                    label="I attest that I have read the End User License Agreement and agree to be bound by the conditions set
                    therein."
                  />
                </div>
              </div>
              <div id="hipaa-area">
                <div className="flex flex-row items-center">
                  {hipaaOpen ? (
                    <NavArrowDown onClick={() => setHipaaOpen(!hipaaOpen)} />
                  ) : (
                    <NavArrowRight onClick={() => setHipaaOpen(!hipaaOpen)} />
                  )}
                  <p className="ml-2 text-2xl font-bold">HIPAA AGREEMENT</p>
                </div>
                {hipaaOpen && (
                  <>
                    <div className="mt-4 h-[30vh] overflow-scroll">
                      <HIPAASectionText text={HIPAA.Statement.One} />
                      <HIPAASectionText text={HIPAA.Statement.Two} />
                      <HIPAASectionText text={HIPAA.Statement.Three} />
                      <div>
                        <form.Field
                          name="exceptions"
                          children={(field) => (
                            <FormInput
                              label="Exceptions"
                              id="hipaa-exceptions"
                              name={field.name}
                              value={field.state.value}
                              handleBlur={field.handleBlur}
                              handleChange={(e) => field.handleChange(e.target.value)}
                              placeholder="none"
                            />
                          )}
                        />
                      </div>
                      <HIPAASectionText text={HIPAA.Statement.Four} />
                      <HIPAASectionText text={HIPAA.Statement.Five} />
                      <HIPAASectionText text={HIPAA.Statement.Six} />
                      <HIPAASectionText text={HIPAA.Statement.Seven} />
                      <HIPAASectionText text={HIPAA.Statement.Eight} />
                    </div>
                  </>
                )}
                <div
                  id="hipaa-attestation-checkbox-container"
                  className="my-4 ml-4 flex w-full flex-row justify-center"
                >
                  <Checkbox
                    id="hipaa-attestation-checkbox"
                    name="hipaa-attestation"
                    onChange={() => {
                      handleHipaaAttestationCheckboxClick();
                    }}
                    checked={attestedHipaa}
                    label="I attest that I have read the HIPAA privacy policy and agree to be bound by the conditions set
                    therein including the exceptions listed above."
                  />
                </div>
              </div>
              <div id="consent-for-care-area">
                <div className="flex flex-row items-center">
                  {consentForCareOpen ? (
                    <NavArrowDown onClick={() => setConsentForCareOpen(!consentForCareOpen)} />
                  ) : (
                    <NavArrowRight onClick={() => setConsentForCareOpen(!consentForCareOpen)} />
                  )}
                  <p className="ml-2 text-2xl font-bold">CONSENT FOR CARE AGREEMENT</p>
                </div>
                {consentForCareOpen && (
                  <div id="consent-for-care-page-page-content" className="flex h-[35vh] flex-col overflow-scroll">
                    <ConsentSectionText text={ConsentForCare.StatementA} />
                    <ConsentSectionText text={ConsentForCare.StatementB} />
                    <ConsentSectionText text={ConsentForCare.StatementC} />
                    <ConsentSectionText text={ConsentForCare.StatementD} />
                    <ConsentSubSectionText text={ConsentForCare.StatementD1} />
                    <ConsentSubSectionText text={ConsentForCare.StatementD2} />
                    <ConsentSubSectionText text={ConsentForCare.StatementD3} />
                    <ConsentSubSectionText text={ConsentForCare.StatementD4} />
                    <ConsentSubSectionText text={ConsentForCare.StatementD5} />
                    <ConsentSectionText text={ConsentForCare.StatementE} />
                  </div>
                )}
                <div
                  id="consent-for-care-attestation-checkbox-container"
                  className="my-4 ml-4 flex w-full flex-row justify-center"
                >
                  <Checkbox
                    id="consent-for-care-attestation-checkbox"
                    name="consent-for-care-attestation"
                    onChange={() => {
                      handleConsentForCareAttestationCheckboxClick();
                    }}
                    checked={attestedConsentForCare}
                    label="I attest that I have read the Consent For Care policy and agree to be bound by the conditions set
                    therein."
                  />
                </div>
              </div>
              <div id="can-text-area" className="my-4">
                <fieldset>
                  <legend className="text-xl font-bold">
                    Do you agree to opt-ln to receive informational and/or scheduling text messages? You may opt-out at
                    any time replying STOP.
                  </legend>
                  <div>
                    <div className="ml-4">
                      <input
                        type="radio"
                        id="Yes"
                        name="canTextStatus"
                        value="yes"
                        checked={canText === 'yes'}
                        onChange={(e) => {
                          setIsSubmitted(false);
                          handleCanTextStatusChange(e.target.value);
                        }}
                      />
                      <label className="ml-2 text-xl" htmlFor="Yes">
                        Yes
                      </label>
                    </div>

                    <div className="ml-4">
                      <input
                        type="radio"
                        id="No"
                        name="canTextStatus"
                        value="no"
                        checked={canText === 'no'}
                        onChange={(e) => handleCanTextStatusChange(e.target.value)}
                      />
                      <label className="ml-2 text-xl" htmlFor="No">
                        No
                      </label>
                    </div>
                  </div>
                </fieldset>
              </div>
              {attestedEULA && attestedHipaa && attestedConsentForCare && (
                <FormsSignatureArea
                  elementId={'patient-forms-sig-'}
                  data={chart}
                  patientId={patientId}
                  isSigning={isSigning}
                  setIsSigning={setIsSigning}
                  sigName="forms"
                  hipaaExceptions={form.getFieldValue('exceptions')}
                />
              )}
            </div>
          </div>
          <div id="nav" className="flex flex-col">
            <Button
              id="submit-info"
              type="button"
              onClick={() => {
                form.handleSubmit();
                setIsSubmitted(true);
              }}
              textContent="Submit"
              disabled={!attestedEULA || !attestedConsentForCare || !attestedHipaa}
              submitted={isSubmitted}
            />
            {patientInfo.navType.base === 'self' &&
              isSubmitted &&
              nextPageURL &&
              attestedEULA &&
              attestedHipaa &&
              attestedConsentForCare &&
              chart?.HIPAA_AUTH?.signature1DataURI?.length > 0 && (
                <NavForward
                  to={nextPageURL}
                  disabled={!attestedEULA || !attestedConsentForCare || !attestedHipaa || !isSubmitted}
                />
              )}
            {patientInfo?.conciergeNavigator?.paymentUnlocked && isSubmitted && (
              <div className="place-self-center">
                <NextSection to={`../../../${currentUser?.uid}/payment/confirmation`} />
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
